import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { Helmet } from 'react-helmet';
import Cookies from 'universal-cookie';
import { useNavigate, useLocation } from 'react-router-dom';
import { analyticsService } from '@common/analytics';
import { firstVisitEventsApi, getClientSettingsApi } from '@common/api/api';
import { appRoutes } from '@app/config/app_routes';
import { facebookPixelScript, facebookPixelNoscript } from '../../actions/facebook_analytics';
import { hotJarInitialize, hotJarIdentifyUser } from '../../actions/hotjar';
import { sectionIds } from '../home_page/prop_types';
import { googleLoginScript } from '../registration/inner_components/google_btn';
import {
  getAppTheme, fromMobileApp, compareObjects, isKA, isPG, isPS
} from '../../../common/config/utils';
import { appThemes } from '../../../common/config/const';
import env from '../../config/variables';
import { isPrerenderMode } from '../../config/util';
import { getSpriteUrl, loadSVGSprite } from '../../../common/sprites/loadSprite';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';
import { useLogout } from '../settings/sign_out/use_logout';
import { checkIfCanMakeApplePayPayments } from '../../actions/purchase_details';

const { shared } = window;

const cookies = new Cookies();

const cookieValue = (valueName) => {
  const valueString = document.cookie.split('; ').find(row => row.startsWith(`${ valueName }=`));
  const value = valueString ? valueString.split('=')[1] : null;
  return value;
};

const { PAYMENT_PROCESSING_INTERVAL_SEC } = env;

const retryRequest = ({
  apiFunction, maxRetries, delay = 2000, attempt = 1
}) => apiFunction().catch(error => {
  if (maxRetries && attempt >= maxRetries) throw error;
  return new Promise(resolve => setTimeout(resolve, delay))
    .then(() => retryRequest({
      apiFunction, maxRetries, delay, attempt: attempt + 1
    }));
});

function AnalyticsAndCommonLogicComponent({
  userId,
  getUser,
  user,
  loadHomePage,
  userPaymentProcessing,
  getMyAccountCredits,
  appLoading,
  getAnonymousUser,
  userFirstActivePromotion,
  initFirebaseDatabase,
  signOutFromFBDatabase,
  setDynamicConfig,
  forceLogout,
  setForceLogout,
  dynamicConfig,
  setGoogleConfig,
  updateUser,
  logOutClear
}) {
  const { routeKey } = useCurrentPath();

  // BroadcastChannel for communication with other tabs
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      log(LOG_LEVELS.INFO, 'BroadcastChannel', 'BroadcastChannel is not supported in this browser');
      return undefined;
    }
    const bc = new BroadcastChannel('currency_changed');
    bc.onmessage = (event) => {
      log(LOG_LEVELS.INFO, 'Get update currency from other tab event', 'currency_changed', { event });

      const { chat, voipChat } = window.store.getState();
      if (![appRoutes.purchaseDetails, appRoutes.advisorBonus].includes(routeKey) && !chat.state && !voipChat.state) {
        window.location.reload();
      }
    };
    return () => bc.close();
  }, [routeKey]);

  const { clientSideLogout } = useLogout({
    onHomePageLoad: () => loadHomePage(true),
    onAnonymousUserSet: getAnonymousUser,
    onLogoutClear: logOutClear,
    onDynamicConfigSet: (clientSettings) => setDynamicConfig(clientSettings, true)
  });

  const setAppTheme = () => {
    const appTheme = getAppTheme();
    if (appTheme === appThemes.dark) {
      document.body.classList.add(`${ shared.darkTheme }`);
    }
  };
  const initializeData = async () => {
    try {
      if (window.currentUserId) {
        const [clientSettingsRes] = await Promise.all([
          retryRequest({ apiFunction: getClientSettingsApi, delay: 2000 }),
          getUser(window.currentUserId)
        ]);
        setDynamicConfig(clientSettingsRes, true);
      } else {
        const [firstVisitRes] = await Promise.all([
          retryRequest({ apiFunction: firstVisitEventsApi, delay: 2000 }),
          getAnonymousUser()
        ]);
        setDynamicConfig(firstVisitRes, true);
      }
    } catch (error) {
      log(LOG_LEVELS.ERROR, 'initializeData', 'Error initializing data with retries:', error);
      try {
        const apiFunction = window.currentUserId ? getClientSettingsApi : firstVisitEventsApi;
        const res = await apiFunction();
        setDynamicConfig(res, true);
      } catch (fallbackError) {
        log(LOG_LEVELS.ERROR, 'initializeData', 'Fallback failed:', fallbackError);
      }
    }
  };

  useEffect(() => {
    let isCleanedUp = false;
    const loadSprites = async () => {
      if (!isCleanedUp) {
        await loadSVGSprite(getSpriteUrl(), 'svg-sprite-container');
      }
    };

    // Small delay to prevent race condition with DOM
    const timeoutId = setTimeout(loadSprites, 10);

    // Cleanup function that prevents deletion
    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      // DON'T clear sprites on cleanup in React 19
    };
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', (event) => {
      log(LOG_LEVELS.DEBUG, 'AnalyticsAndCommonLogicComponent', 'popstate', { event });
    });
    return () => {
      window.removeEventListener('popstate', (event) => {
        log(LOG_LEVELS.DEBUG, 'return from AnalyticsAndCommonLogicComponent', 'popstate', { event });
      });
    };
  }, []);

  useEffect(() => {
    setAppTheme();
    setTimeout(() => appLoading(), 1000);
    if (!cookieValue('visited')) {
      analyticsService.trackEvent('first visit');
      cookies.set('visited', true, {
        path: '/'
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      await initializeData();
      analyticsService.init();
      hotJarInitialize();
      // Initialize Apple Pay eligibility early to avoid race conditions
      checkIfCanMakeApplePayPayments();
    })();
    if (!fromMobileApp && !isPrerenderMode() && routeKey !== appRoutes.home) {
      loadHomePage(false, { section_ids: sectionIds.salesBanner });
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      signOutFromFBDatabase();
      return;
    }

    (async () => {
      try {
        const clientSettingsRes = await retryRequest({ apiFunction: getClientSettingsApi, delay: 2000 });
        setDynamicConfig(clientSettingsRes, true);
      } catch (error) {
        log(LOG_LEVELS.ERROR, 'initializeData', 'Error fetching client settings after login:', error);
      }
    })();

    analyticsService.authorize(userId, { mixpanelAliasCreated: user.mixpanelAliasCreated, onAliasCreated: () => updateUser({ mixpanelAliasCreated: true }) });
    initFirebaseDatabase(user);
  }, [userId]);

  useEffect(() => {
    if (!userId || !dynamicConfig) return;
    hotJarIdentifyUser({ user });
  }, [user, dynamicConfig]);

  useEffect(() => {
    if (userPaymentProcessing) {
      const interval = setInterval(() => {
        getMyAccountCredits();
      }, PAYMENT_PROCESSING_INTERVAL_SEC * 1000);
      return () => {
        if (interval) clearInterval(interval);
      };
    }
    return () => { };
  }, [userPaymentProcessing]);

  useEffect(() => {
    if (forceLogout && userId) {
      log(LOG_LEVELS.INFO, 'Auth', 'Processing automatic logout due to 401 error');
      setForceLogout(false);
      clientSideLogout();
    }
    return () => {
      if (forceLogout) {
        setForceLogout(false);
      }
    };
  }, [forceLogout]);

  const history = useNavigate();
  const location = useLocation();

  const [userFirstActivePromotionState, setUserFirstActivePromotionState] = useState(userFirstActivePromotion);

  const shouldUpdatePageWithAdvisors = useMemo(() => !compareObjects({ ...userFirstActivePromotion, promoTimer: undefined }, { ...userFirstActivePromotionState, promoTimer: undefined })
    && (userFirstActivePromotion.refreshStrategy === 'all_advisors'), [userFirstActivePromotion, userFirstActivePromotionState]);

  useEffect(() => {
    if (shouldUpdatePageWithAdvisors && !isPrerenderMode()) {
      setUserFirstActivePromotionState(prevState => {
        if (compareObjects(userFirstActivePromotion, prevState)) return prevState;
        return {
          ...prevState,
          ...userFirstActivePromotion
        };
      });
      if (window.fromCacheRestored) {
        delete window.fromCacheRestored;
        return;
      }
      history(`${ location.pathname }${ location.search }`, {
        state: {
          ...location.state, needReloadHomePage: true, needReloadSearchResults: true, needReloadAdvisorPage: true
        },
        replace: true
      });
    }
  }, [shouldUpdatePageWithAdvisors]);

  const addFacebookPixelNoscript = () => {
    if (!document.getElementById('facebook_pixel_noscript')) {
      document.body.appendChild(facebookPixelNoscript());
    }
  };

  const addScriptsToBody = () => {
    addFacebookPixelNoscript();
  };

  const onChangeClientState = useCallback(() => {
    const scriptElem = document.getElementById('googleLoginBtnScript');
    if (scriptElem !== null) {
      scriptElem.onload = () => setGoogleConfig(window.google);
    }
  }, [setGoogleConfig]);

  const addScripts = () => (
    <>
      <Helmet onChangeClientState={ onChangeClientState }>
        {/* Google Fonts - preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin />

        {/* Google Fonts - load font families based on project */}
        {isKA() && (
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
            rel="stylesheet"
          />
        )}
        {isPG() && (
          <link
            href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&display=swap"
            rel="stylesheet"
          />
        )}
        {isPS() && (
          <link
            href="https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;600;700&display=swap"
            rel="stylesheet"
          />
        )}

        {facebookPixelScript()}
        {googleLoginScript()}
      </Helmet>
      {addScriptsToBody()}
      <div
        id="svg-sprite-container"
        style={ { position: 'absolute', top: '-1500px' } }
        suppressHydrationWarning
      />
    </>
  );

  return addScripts();
}

export default AnalyticsAndCommonLogicComponent;
